import React, { useState, useEffect, useMemo } from "react";
import "./EventLog.css";
import MtiButton from "../../Buttons/MtiButton";
import EventLogApi from "../../../api/EventLogApi";
import DatePicker from "./DatePicker";
import LightPathUtil from "../../../Utility/LightPathUtil";
import EventLogChart from "./EventLogChart";
import CommonTable from "../../Common/CommonTable";
import { Column, Row } from "react-table";
import { EventLog } from "../../../Model/EventLog";
import EventLogUtil from "../../../Utility/EventLogUtil";
import DateUtil from "../../../Utility/DateUtil";

type Props = {
  isEventLogChartShowed: boolean;
  setIsEventLogChartShowed: (show: boolean) => void;
  setEventLogTextExport: React.Dispatch<React.SetStateAction<string>>;
  style?: React.CSSProperties;
};

export default function EventLogPage({
  isEventLogChartShowed,
  setIsEventLogChartShowed,
  setEventLogTextExport,
  style,
}: Props) {
  const [startDate, setStartDate] = useState(new Date());
  const [maxStartDate, setMaxStartDate] = useState(new Date());
  const [stopDate, setStopDate] = useState(new Date());
  const [eventLogs, setEventLogs] = useState([] as EventLog[]);
  const [filteredEventLogs, setFilteredEventLog] = useState([] as EventLog[]);
  const [clearFilter, setClearFilter] = useState({
    action: () => { },
  });

  const sortByTimeFn = (rowA: Row<EventLog>, rowB: Row<EventLog>, columnId: string, desc?: boolean) => {

    const dateA = new Date(rowA.original.createdDate);
    const dateB = new Date(rowB.original.createdDate);

    if (dateA === dateA) {
      return 0;
    } else if (dateA > dateB) {
      return -1;
    } else {
      return 1;
    }
  };

  const getTableColumnConfig: Column<EventLog>[] = useMemo(
    () => [
      {
        id: "time",
        Header: "Time",
        accessor: (p) => {
          return p.createdDate;
        },
        sortType: sortByTimeFn,
      },
      {
        id: "eventType",
        Header: "Event Type",
        accessor: (p) => {
          return p.description;
        },
      },
      {
        id: "plateID",
        Header: "Plate ID",
        accessor: (p) => {
          return p.plateId;
        },
      },
      {
        id: "inpId",
        Header: "Inp. ID",
        accessor: (p) => {
          return p.imagingId;
        },
      },
      {
        id: "plateType",
        Header: "Plate Type",
        accessor: (p) => {
          return p.plateTypeName;
        },
      },
      {
        id: "lightPath",
        Header: "Light Path",
        accessor: (p) => {
          return LightPathUtil.toName(p.lightPath);
        },
      },
      {
        id: "setting",
        Header: "Setting",
        accessor: (p) => {
          return p.captureProfileName;
        },
      },
      {
        id: "project",
        Header: "Project",
        accessor: (p) => {
          return p.projectName;
        },
      },
      {
        id: "experiment",
        Header: "Experiment",
        accessor: (p) => {
          return p.experimentName;
        },
      },
      {
        id: "userName",
        Header: "User Name",
        accessor: (p) => {
          return p.userName;
        },
      },
      {
        id: "userGroup",
        Header: "Group",
        accessor: (p) => {
          return p.userGroup;
        },
      },
      {
        id: "Duration",
        Header: "Duration",
        accessor: (p) => {
          return DateUtil.convertMinutesToHHMMSS(p.durationInMinute);
        },
      },
    ],
    []
  );

  const onSubmitFormDatePicker = () => {
    EventLogApi.getBetweenEventLog(startDate, stopDate).then((data) => {
      setEventLogs(data);
    });
  };

  useEffect(() => {
    if (startDate > stopDate) {
      setStartDate(stopDate);
    }
    setMaxStartDate(stopDate);
  }, [stopDate]);

  useEffect(() => {
    setEventLogTextExport(EventLogUtil.exportToPlainText(filteredEventLogs));
  }, [filteredEventLogs]);

  useEffect(() => {
    EventLogApi.getBetweenEventLog(startDate, stopDate).then((data) => {
      setEventLogs(data);
    });
  }, []);

  return (
    <div
      style={{
        height: "100%",
        overflow: "hidden",
        padding: "22px 28px 0px 25px",
        ...style,
      }}
    >
      <div className="event-log-date-picker-form">
        <div
          style={{
            display: "flex",
          }}
        >
          <label className="event-log-form-label"> Start Date </label>
          <DatePicker date={startDate} maxDate={maxStartDate} setDate={setStartDate} />
          <label className="event-log-form-label"> End Date </label>
          <DatePicker date={stopDate} maxDate={new Date()} setDate={setStopDate} />
          <MtiButton
            onClick={onSubmitFormDatePicker}
            disabled={false}
            style={{
              width: 100,
              display: "inline-block",
            }}
          >
            SEARCH
          </MtiButton>
        </div>
      </div>

      <CommonTable<EventLog>
        setClearFilter={setClearFilter}
        data={eventLogs}
        columns={getTableColumnConfig}
        enableSelection={false}
        style={{ height: "calc(100% - 67px)" }}
        pageLineSize={11}
        setFilteredRow={setFilteredEventLog}
        sortingRules={[
          {
            id: "time",
          },
        ]}
        showTotalItem={{ show: true, totalText: "Total Events: " }}
      />

      <EventLogChart eventLogs={eventLogs} setShowChart={setIsEventLogChartShowed} showChart={isEventLogChartShowed} />
    </div>
  );
}
